import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TourScreens } from "@/components/admin/TourScreens";
import { TourStats } from "@/components/admin/TourStats";
import { PressStats } from "@/components/admin/PressStats";
import { WatchThenUpgrade } from "@/components/admin/WatchThenUpgrade";
import { SharerPrizes } from "@/components/admin/SharerPrizes";
import { LanguageDemand } from "@/components/admin/LanguageDemand";
import { GuideStats } from "@/components/admin/GuideStats";
import { Announcements } from "@/components/admin/Announcements";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid={testid}>
    <p className="text-xs uppercase tracking-widest text-ink-soft">{label}</p>
    <p className="mt-2 font-display text-3xl font-extrabold tracking-tighter">{value}</p>
  </div>
);

const AXIS_TICK = { fontSize: 11 };
const BAR_RADIUS = [8, 8, 0, 0];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", description: "", percent_off: 20 });
  const [banks, setBanks] = useState([]);
  const [lp, setLp] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [milu, setMilu] = useState(null);
  const [digest, setDigest] = useState(null);
  const [comeback, setComeback] = useState(null);
  const [nudging, setNudging] = useState(false);
  const [sending, setSending] = useState(false);

  const loadDigest = () =>
    api.get("/admin/wingman/digest").then(({ data }) => setDigest(data)).catch(() => {});

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/wingman/digest/send", {});
      toast[data.sent ? "success" : "error"](
        data.sent
          ? "Weekly digest emailed"
          : data.reason === "email_not_configured"
          ? "Add your SendGrid key to send emails"
          : data.reason === "sender_not_verified"
          ? data.detail || "Verify your sender address in SendGrid first"
          : `Could not send: ${data.reason || "unknown error"}`,
      );
      loadDigest();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const loadMilu = () =>
    api.get("/admin/wingman/stats").then(({ data }) => setMilu(data)).catch(() => {});

  const loadComeback = () =>
    api.get("/admin/wingman/comeback").then(({ data }) => setComeback(data)).catch(() => {});

  const sendComeback = async () => {
    setNudging(true);
    try {
      const { data } = await api.post("/admin/wingman/comeback/send", {});
      toast[data.sent ? "success" : "error"](
        data.sent
          ? `MiLu nudged ${data.sent} ${data.sent === 1 ? "person" : "people"}`
          : data.candidates === 0
          ? "Nobody is waiting for a nudge right now"
          : data.failed?.[0]?.reason === "email_not_configured"
          ? "Add your SendGrid key to send emails"
          : `Could not send: ${data.failed?.[0]?.reason || "unknown error"}`,
      );
      loadComeback();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setNudging(false);
    }
  };

  const loadPayments = async () => {
    const [b, c, t] = await Promise.all([
      api.get("/localpay/banks"), api.get("/admin/localpay/config"),
      api.get("/admin/localpay/transfers"),
    ]);
    setBanks(b.data.banks);
    setLp(c.data);
    setTransfers(t.data);
  };

  const saveLp = async () => {
    try {
      const { data } = await api.put("/admin/localpay/config", {
        ...lp,
        usd_to_vnd: Number(lp.usd_to_vnd),
      });
      setLp(data);
      toast.success("Bank transfer settings saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const decide = async (ref, action) => {
    try {
      await api.post(`/admin/localpay/${ref}/${action}`);
      toast.success(action === "approve" ? "Transfer approved" : "Transfer rejected");
      loadPayments();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const loadAll = async () => {
    const [s, u, f, p] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"),
      api.get("/admin/feedback"), api.get("/admin/promotions"),
    ]);
    setStats(s.data);
    setUsers(u.data);
    setFeedback(f.data);
    setPromos(p.data);
  };

  useEffect(() => {
    loadAll().catch((e) => toast.error(apiError(e.response?.data?.detail)));
    loadPayments().catch(() => {});
    loadMilu();
    loadDigest();
    loadComeback();
  }, []);

  const setPlan = async (user_id, plan) => {
    await api.put(`/admin/users/${user_id}/plan`, { plan });
    toast.success("Plan updated");
    loadAll();
  };

  const addPromo = async () => {
    if (!form.code.trim() || !form.description.trim()) return;
    await api.post("/admin/promotions", { ...form, percent_off: Number(form.percent_off) });
    setForm({ code: "", description: "", percent_off: 20 });
    toast.success("Promotion created");
    loadAll();
  };

  if (!stats) return <div className="p-16 text-center text-sm text-ink-soft">Loading dashboard…</div>;

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="admin-page">
      <h1 className="font-display text-4xl font-extrabold tracking-tighter">Admin dashboard</h1>
      <p className="mt-2 text-sm text-ink-soft">Users, subscriptions, promotions and engagement analytics.</p>

      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total users" value={stats.total_users} testid="stat-users" />
        <Stat label="New (7 days)" value={stats.new_users_7d} testid="stat-new-users" />
        <Stat label="AI messages" value={stats.ai_messages} testid="stat-ai-messages" />
        <Stat label="Revenue" value={`$${stats.revenue}`} testid="stat-revenue" />
        <Stat label="Translated messages" value={stats.room_messages} testid="stat-room-messages" />
        <Stat label="Rooms" value={stats.rooms} testid="stat-rooms" />
        <Stat label="Cached translations" value={stats.translations_cached} testid="stat-translations" />
        <Stat label="Avg rating" value={stats.avg_rating ?? "—"} testid="stat-rating" />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="chart-languages">
          <p className="font-display text-lg font-bold tracking-tight">Users by language</p>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.language_breakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="language" tick={AXIS_TICK} />
                <YAxis allowDecimals={false} tick={AXIS_TICK} />
                <Tooltip />
                <Bar dataKey="count" fill="#1A7DFF" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="chart-plans">
          <p className="font-display text-lg font-bold tracking-tight">Subscription mix</p>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(stats.plan_counts).map(([plan, count]) => ({ plan, count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="plan" tick={AXIS_TICK} />
                <YAxis allowDecimals={false} tick={AXIS_TICK} />
                <Tooltip />
                <Bar dataKey="count" fill="#22B8FF" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <Tabs defaultValue="users" className="mt-10">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos">Promotions</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Bank transfers</TabsTrigger>
          <TabsTrigger value="milu" data-testid="tab-milu">MiLu</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="overflow-x-auto rounded-3xl border border-white/10 bg-panel" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead className="bg-page text-xs uppercase tracking-widest text-ink-soft">
                <tr>
                  {["Name", "Email", "Lang", "Plan", "Role", "Messages", "Change plan"].map((h) => (
                    <th key={h} className="px-4 py-3 text-start">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-white/10" data-testid={`admin-user-${u.user_id}`}>
                    <td className="px-4 py-3">{u.name}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3">{u.language}</td>
                    <td className="px-4 py-3 font-semibold">{u.plan}</td>
                    <td className="px-4 py-3">{u.role}</td>
                    <td className="px-4 py-3">{u.message_count || 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        {["free", "basic", "premium"].map((p) => (
                          <button
                            key={p}
                            data-testid={`set-plan-${p}-${u.user_id}`}
                            onClick={() => setPlan(u.user_id, p)}
                            className="rounded-full bg-surfacealt px-3 py-1 text-xs hover:bg-brand hover:text-white"
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="promos">
          <div className="rounded-3xl border border-white/10 bg-panel p-6">
            <div className="grid gap-3 sm:grid-cols-4">
              <Input
                data-testid="promo-code-input" placeholder="CODE" value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="min-h-[44px] rounded-xl"
              />
              <Input
                data-testid="promo-description-input" placeholder="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="min-h-[44px] rounded-xl sm:col-span-2"
              />
              <Input
                data-testid="promo-percent-input" type="number" min={1} max={100} value={form.percent_off}
                onChange={(e) => setForm({ ...form, percent_off: e.target.value })}
                className="min-h-[44px] rounded-xl"
              />
            </div>
            <Button
              data-testid="create-promo-button" onClick={addPromo}
              className="mt-4 min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover active:scale-95"
            >
              Create promotion
            </Button>
            <ul className="mt-6 space-y-2" data-testid="promo-list">
              {promos.map((p) => (
                <li key={p.promo_id} className="flex items-center justify-between gap-3 rounded-2xl bg-page px-4 py-3 text-sm">
                  <span>
                    <b>{p.code}</b> · {p.percent_off}% off · {p.description}
                  </span>
                  <button
                    data-testid={`delete-promo-${p.promo_id}`}
                    onClick={async () => {
                      await api.delete(`/admin/promotions/${p.promo_id}`);
                      loadAll();
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-ink-soft hover:text-destructive" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>

        <TabsContent value="payments">
          {lp && (
            <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="localpay-config">
              <p className="font-display text-lg font-bold tracking-tight">
                Receive real money in Vietnam (VietQR)
              </p>
              <p className="mt-1 text-sm text-ink-soft">
                Payers scan a NAPAS 247 QR with any Vietnamese bank app and transfer straight to this account.
                {lp.auto_confirm
                  ? " Incoming credits are matched to their reference automatically — nothing for you to do."
                  : " You approve each transfer below and the plan or donation is applied instantly."}
              </p>
              <p className="mt-3 text-xs" data-testid="localpay-autoconfirm">
                <span
                  className={`rounded-full px-2.5 py-1 font-semibold ${
                    lp.auto_confirm ? "bg-forest/20 text-forest" : "bg-white/10 text-ink-soft"
                  }`}
                >
                  {lp.auto_confirm ? "Auto-confirm active (Casso)" : "Manual approval"}
                </span>
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-ink-soft">Bank</label>
                  <select
                    data-testid="localpay-bank"
                    value={lp.bank_code}
                    onChange={(e) => setLp({ ...lp, bank_code: e.target.value })}
                    className="mt-2 min-h-[44px] w-full rounded-xl border border-white/15 bg-surfacealt px-3 text-sm"
                  >
                    <option value="">Select a bank</option>
                    {banks.map((b) => (
                      <option key={b.code} value={b.code}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ink-soft">Account number</label>
                  <Input
                    data-testid="localpay-account"
                    value={lp.account_no}
                    onChange={(e) => setLp({ ...lp, account_no: e.target.value })}
                    className="mt-2 min-h-[44px] rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ink-soft">Account holder</label>
                  <Input
                    data-testid="localpay-holder"
                    value={lp.account_name}
                    onChange={(e) => setLp({ ...lp, account_name: e.target.value })}
                    className="mt-2 min-h-[44px] rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-ink-soft">USD → VND rate</label>
                  <Input
                    data-testid="localpay-rate"
                    type="number"
                    value={lp.usd_to_vnd}
                    onChange={(e) => setLp({ ...lp, usd_to_vnd: e.target.value })}
                    className="mt-2 min-h-[44px] rounded-xl"
                  />
                </div>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    data-testid="localpay-enabled"
                    type="checkbox"
                    checked={lp.enabled}
                    onChange={(e) => setLp({ ...lp, enabled: e.target.checked })}
                    className="h-4 w-4 accent-[#1A7DFF]"
                  />
                  Show bank transfer to customers
                </label>
                <Button
                  data-testid="localpay-save"
                  onClick={saveLp}
                  className="min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover"
                >
                  Save
                </Button>
              </div>
            </div>
          )}

          <div
            className="mt-6 overflow-x-auto rounded-3xl border border-white/10 bg-panel"
            data-testid="localpay-transfers"
          >
            <table className="w-full text-sm">
              <thead className="text-start text-xs uppercase tracking-widest text-ink-soft">
                <tr>
                  <th className="px-5 py-4 text-start">Reference</th>
                  <th className="px-5 py-4 text-start">For</th>
                  <th className="px-5 py-4 text-start">Amount</th>
                  <th className="px-5 py-4 text-start">Payer</th>
                  <th className="px-5 py-4 text-start">Status</th>
                  <th className="px-5 py-4 text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((t) => (
                  <tr key={t.ref} className="border-t border-white/10" data-testid={`transfer-${t.ref}`}>
                    <td className="px-5 py-4 font-semibold">{t.ref}</td>
                    <td className="px-5 py-4 text-ink-soft">
                      {t.kind === "plan" ? `${t.plan_id}${t.seats ? ` × ${t.seats}` : ""}` : "Donation"}
                    </td>
                    <td className="px-5 py-4">
                      ${t.amount_usd}
                      <span className="ms-2 text-xs text-ink-soft">
                        {new Intl.NumberFormat("vi-VN").format(t.amount_vnd)} ₫
                      </span>
                    </td>
                    <td className="px-5 py-4 text-ink-soft">{t.payer_name || t.email || "—"}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs ${
                          t.status === "approved"
                            ? "bg-forest/20 text-forest"
                            : t.status === "awaiting_review"
                            ? "bg-brand/20 text-brand"
                            : "bg-white/10 text-ink-soft"
                        }`}
                      >
                        {t.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-end">
                      {t.status !== "approved" && (
                        <div className="flex justify-end gap-2">
                          <Button
                            data-testid={`approve-${t.ref}`}
                            onClick={() => decide(t.ref, "approve")}
                            className="min-h-[36px] rounded-full bg-brand px-4 text-xs hover:bg-brand-hover"
                          >
                            Approve
                          </Button>
                          <Button
                            data-testid={`reject-${t.ref}`}
                            variant="outline"
                            onClick={() => decide(t.ref, "reject")}
                            className="min-h-[36px] rounded-full px-4 text-xs"
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {transfers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-sm text-ink-soft">
                      No bank transfers yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="milu">
          <div className="mb-6 space-y-4">
            <TourStats />
            <WatchThenUpgrade />
            <SharerPrizes />
            <Announcements />
            <GuideStats />
            <LanguageDemand />
            <PressStats />
            <TourScreens />
          </div>
          {milu && (
            <div data-testid="milu-stats">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Conversations" value={milu.conversations} testid="milu-conversations" />
                <Stat label="Visitor messages" value={milu.messages} testid="milu-messages" />
                <Stat label="Voice messages" value={milu.voice_messages} testid="milu-voice" />
                <Stat label="Plan clicks" value={milu.cta_clicks} testid="milu-clicks" />
                <Stat label="Click rate" value={`${milu.click_rate}%`} testid="milu-clickrate" />
                <Stat label="Plans sold via MiLu" value={milu.plans.count} testid="milu-plans" />
                <Stat label="Plan revenue" value={`$${milu.plans.revenue}`} testid="milu-revenue" />
                <Stat
                  label="Donations via MiLu"
                  value={`${milu.donations.count} · $${milu.donations.revenue}`}
                  testid="milu-donations"
                />
              </div>

              {milu.by_day.length > 0 && (
                <div
                  className="mt-6 rounded-3xl border border-white/10 bg-panel p-6"
                  data-testid="milu-byday"
                >
                  <p className="font-display text-lg font-bold tracking-tight">Last 14 days</p>
                  <div className="mt-5 flex h-32 items-end gap-2">
                    {milu.by_day.map((d) => {
                      const peak = Math.max(...milu.by_day.map((x) => x.messages), 1);
                      return (
                        <div key={d.day} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                          <div
                            title={`${d.day}: ${d.messages}`}
                            style={{ height: `${Math.max((d.messages / peak) * 90, 4)}%` }}
                            className="w-full rounded-t bg-brand/70 transition-all hover:bg-brand"
                          />
                          <span className="text-[10px] text-ink-soft">{d.day.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div
                className="mt-6 rounded-3xl border border-white/10 bg-panel p-6"
                data-testid="milu-digest"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-lg font-bold tracking-tight">Weekly digest</p>
                    <p className="mt-1 text-sm text-ink-soft">
                      Emailed to you every Monday morning — what MiLu sold and what people asked him.
                    </p>
                  </div>
                  <Button
                    data-testid="milu-digest-send"
                    onClick={sendDigest}
                    disabled={sending}
                    className="min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover"
                  >
                    {sending ? "Sending…" : "Email it to me now"}
                  </Button>
                </div>
                {digest && (
                  <>
                    <div className="mt-5 grid gap-3 sm:grid-cols-3">
                      <p className="text-sm text-ink-soft">
                        Last 7 days:{" "}
                        <b className="text-ink" data-testid="digest-conversations">
                          {digest.digest.conversations}
                        </b>{" "}
                        conversations
                      </p>
                      <p className="text-sm text-ink-soft">
                        <b className="text-ink" data-testid="digest-plans">
                          {digest.digest.plans.count}
                        </b>{" "}
                        plans · ${digest.digest.plans.revenue}
                      </p>
                      <p className="text-sm text-ink-soft">
                        <b className="text-ink">{digest.digest.cta_clicks}</b> clicks to plans (
                        {digest.digest.click_rate}%)
                      </p>
                    </div>
                    {!digest.email_configured && (
                      <p
                        className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200"
                        data-testid="digest-email-warning"
                      >
                        Email sending is off — add SENDGRID_API_KEY and SENDER_EMAIL to start receiving this
                        every Monday. The numbers above always work.
                      </p>
                    )}
                    {digest.email_configured && digest.last_sent?.result?.reason === "sender_not_verified" && (
                      <p
                        className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200"
                        data-testid="digest-sender-warning"
                      >
                        SendGrid is connected but it refuses to send from your sender address. Verify it in
                        SendGrid → Settings → Sender Authentication, then hit send again.
                      </p>
                    )}
                    {digest.last_sent && (
                      <p className="mt-3 text-xs text-ink-soft" data-testid="digest-last-sent">
                        Last attempt: {new Date(digest.last_sent.created_at).toLocaleString()} →{" "}
                        {digest.last_sent.result?.sent ? "sent" : digest.last_sent.result?.reason}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div
                className="mt-6 rounded-3xl border border-white/10 bg-panel p-6"
                data-testid="milu-comeback"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-lg font-bold tracking-tight">Come-back nudge</p>
                    <p className="mt-1 text-sm text-ink-soft">
                      MiLu emails anyone who signed up {comeback?.after_days ?? 3}+ days ago and never
                      sent a message. Runs itself once a day — one email per person, ever.
                    </p>
                  </div>
                  <Button
                    data-testid="milu-comeback-send"
                    onClick={sendComeback}
                    disabled={nudging || !comeback?.waiting}
                    className="min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover"
                  >
                    {nudging ? "Sending…" : "Nudge them now"}
                  </Button>
                </div>
                {comeback && (
                  <>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      <p className="text-sm text-ink-soft">
                        <b className="text-ink" data-testid="comeback-waiting">{comeback.waiting}</b>{" "}
                        waiting for a nudge
                      </p>
                      <p className="text-sm text-ink-soft">
                        <b className="text-ink" data-testid="comeback-sent-total">{comeback.sent_total}</b>{" "}
                        nudged so far
                      </p>
                    </div>
                    {comeback.people.length > 0 && (
                      <ul className="mt-4 space-y-2" data-testid="comeback-people">
                        {comeback.people.map((p) => (
                          <li
                            key={p.email}
                            className="border-b border-white/5 pb-2 text-sm text-ink-soft last:border-0"
                          >
                            {p.name || "—"} · {p.email}
                            {p.created_at && (
                              <span className="text-xs text-ink-soft/70">
                                {" "}· joined {new Date(p.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {!comeback.email_configured && (
                      <p
                        className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200"
                        data-testid="comeback-email-warning"
                      >
                        Email sending is off — add SENDGRID_API_KEY and SENDER_EMAIL to start nudging.
                      </p>
                    )}
                    {comeback.last_sent && (
                      <p className="mt-3 text-xs text-ink-soft" data-testid="comeback-last-sent">
                        Last attempt: {new Date(comeback.last_sent.created_at).toLocaleString()} →{" "}
                        {comeback.last_sent.result?.sent ? "sent" : comeback.last_sent.result?.reason}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="mt-6 rounded-3xl border border-white/10 bg-panel p-6">
                <p className="font-display text-lg font-bold tracking-tight">
                  What visitors are asking MiLu
                </p>
                <ul className="mt-4 space-y-2" data-testid="milu-questions">
                  {milu.recent_questions.map((q, i) => (
                    <li key={`${i}-${q.slice(0, 24)}`} className="border-b border-white/5 pb-2 text-sm text-ink-soft last:border-0">
                      {q}
                    </li>
                  ))}
                  {milu.recent_questions.length === 0 && (
                    <li className="text-sm text-ink-soft">Nobody has talked to MiLu yet.</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="feedback">
          <ul className="space-y-3" data-testid="admin-feedback-list">
            {feedback.map((f) => (
              <li key={f.feedback_id} className="rounded-3xl border border-white/10 bg-panel p-5 text-sm">
                <p className="text-xs text-ink-soft">
                  {f.email} · {f.category} · {f.rating}★
                </p>
                <p dir="auto" className="mt-1.5">{f.message}</p>
              </li>
            ))}
            {feedback.length === 0 && <p className="text-sm text-ink-soft">No feedback yet.</p>}
          </ul>
        </TabsContent>
      </Tabs>
    </div>
  );
}
